import { useHistory, useParams } from 'react-router-dom';

import PropTypes from 'prop-types';

import {
  Box,
  Slider,
  SliderThumb,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import TimelineValueLabelComponent from './TimelineValueLabelComponent';

import { formatMarks, getMinMaxMarksValue } from './utils';

import { TIME_SLOTS_CONTAINER_ID } from './constants';

const TimelineSlider = styled(Slider)(({ theme }) => ({
  height: 26,
  padding: '1.5rem 0',
  zIndex: 1,

  '& .MuiSlider-thumb': {
    position: 'relative',
    height: 0,
    width: 0,
    marginLeft: 0,
    backgroundColor: '#fff',
    '&:focus, &:hover, &$active, &.Mui-focusVisible': {
      boxShadow: 'none',
    },
    '& input[type="range" i]': {},
    '& .indicator-rectangle': {
      position: 'absolute',
      top: -13,
      width: 4,
      height: 35,
      backgroundColor: theme.palette.secondary.main,
      zIndex: 1,
      cursor: 'pointer',
    },
    '& .indicator-circle': {
      position: 'absolute',
      top: 21,
      width: 10,
      height: 10,
      borderRadius: 45,
      backgroundColor: theme.palette.secondary.main,
      zIndex: 1,
      cursor: 'pointer',
    },
  },
  '& .MuiSlider-valueLabel': {
    left: 'calc(-50% + 1.2rem)',
    top: -22,
  },
  '& .MuiSlider-track': {
    height: 26,
    backgroundColor: '#cfcfcf',
    opacity: 0,
  },
  '& .MuiSlider-rail': {
    height: 26,
    backgroundColor: '#cfcfcf',
    opacity: 0,
    borderRadius: 0,
  },
  '& .MuiSlider-markLabel': {
    top: -5,
    fontSize: '1.2rem',
  },
  '& .MuiSlider-mark': {
    width: 0,
  },
  '& .MuiSlider-markActive': {
    width: 0,
  },
}));

export const StyledTimeSlotsContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '1.5rem',
  width: '100%',
  height: '2.6rem',
  display: 'flex',
  background: '#cfcfcf',
  overflow: 'hidden',
}));

export const StyledGoLive = styled(Typography)(() => ({
  position: 'absolute',
  top: 75,
  width: 50,
  height: 'auto',
  borderRadius: 45,
  zIndex: 1,
  cursor: 'pointer',
}));

const TimelineThumbComponent = (props) => {
  const { children, isDisabled, ...other } = props;

  const history = useHistory();
  const { buildingId } = useParams();

  const navigateToLive = () =>
    history.push(`/building/${buildingId}/all-doors-and-cameras`);

  return (
    <SliderThumb {...other}>
      {children}
      <div className="indicator-rectangle" />
      <div className="indicator-circle" />
      {isDisabled && (
        <Tooltip title="There is no more data to upload" arrow>
          <StyledGoLive onClick={navigateToLive}>Go live</StyledGoLive>
        </Tooltip>
      )}
    </SliderThumb>
  );
};

const VideoTimelineComponent = (props) => {
  const { value, setValue, marks, isNextTimeDisabled } = props;

  const theme = useTheme();

  const breakpoints = {
    isXl: useMediaQuery(theme.breakpoints.up('xl')),
    isLg: useMediaQuery(theme.breakpoints.up('lg')),
    isMD: useMediaQuery(theme.breakpoints.up('md')),
    isXs: useMediaQuery(theme.breakpoints.up('xs')),
  };

  const onChange = (event, newValue) => setValue(newValue);

  const { minTimelineValue, maxTimelineValue } = getMinMaxMarksValue(marks);

  const formattedMarks = formatMarks(marks, breakpoints);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <TimelineSlider
        value={value}
        onChange={onChange}
        marks={formattedMarks}
        step={1}
        min={minTimelineValue}
        max={maxTimelineValue}
        valueLabelDisplay="on"
        components={{
          Thumb: TimelineThumbComponent,
          ValueLabel: TimelineValueLabelComponent,
        }}
        componentsProps={{
          thumb: {
            isDisabled: isNextTimeDisabled,
          },
        }}
      />
      <StyledTimeSlotsContainer id={TIME_SLOTS_CONTAINER_ID} />
    </Box>
  );
};
VideoTimelineComponent.defaultProps = {
  marks: [],
};
VideoTimelineComponent.propTypes = {
  value: PropTypes.number.isRequired,
  setValue: PropTypes.func.isRequired,
  marks: PropTypes.instanceOf(Array),
  isNextTimeDisabled: PropTypes.bool.isRequired,
};

TimelineThumbComponent.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

export default VideoTimelineComponent;
